/**
 * Observability Types
 * JAISIU-822: Phase 5 - Observability & Debugging
 */

/**
 * Execution states for observability
 */
export enum ObservableExecutionState {
  Pending = "pending",
  Running = "running",
  Paused = "paused",
  Completed = "completed",
  Failed = "failed",
  Cancelled = "cancelled",
}

/**
 * Node execution status
 */
export enum NodeStatus {
  Pending = "pending",
  Running = "running",
  Completed = "completed",
  Failed = "failed",
  Skipped = "skipped",
}

/**
 * Log severity levels
 */
export enum ObservableLogLevel {
  Debug = 0,
  Info = 1,
  Warning = 2,
  Error = 3,
  Critical = 4,
}

/**
 * Log entry for observability
 */
export interface ObservableLogEntry {
  id: string;
  timestamp: number;
  level: ObservableLogLevel;
  category: string;
  message: string;
  executionId?: string;
  nodeId?: string;
  agentId?: string;
  data?: Record<string, unknown>;
  traceId?: string;
  spanId?: string;
}

/**
 * Tool invocation record
 */
export interface ToolInvocationRecord {
  id: string;
  toolName: string;
  agentId: string;
  nodeId: string;
  executionId: string;
  input: unknown;
  output?: unknown;
  error?: string;
  startTime: number;
  endTime: number;
  duration: number;
  success: boolean;
  tokensUsed?: number;
  costUsed?: number;
}

/**
 * Aggregated tool statistics
 */
export interface ToolStatistics {
  toolName: string;
  invocationCount: number;
  successCount: number;
  failureCount: number;
  totalDuration: number;
  averageDuration: number;
  minDuration: number;
  maxDuration: number;
  successRate: number;
  totalTokens?: number;
  totalCost?: number;
}

/**
 * Agent execution metrics
 */
export interface AgentMetrics {
  agentId: string;
  nodeId: string;
  name: string;
  role: string;
  status: NodeStatus;
  startTime?: number;
  endTime?: number;
  duration?: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cost: number;
  toolCallCount: number;
  errorCount: number;
  warnings: string[];
  messages: AgentMessageRecord[];
  toolInvocations: ToolInvocationRecord[];
}

/**
 * Agent message record
 */
export interface AgentMessageRecord {
  id: string;
  from: string;
  to: string;
  type: string;
  content?: string;
  timestamp: number;
  tokens?: number;
  cost?: number;
}

/**
 * Cost record for tracking
 */
export interface CostRecord {
  nodeId: string;
  agentId: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  timestamp: number;
  executionId: string;
}

/**
 * Cost summary
 */
export interface CostSummary {
  totalCost: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  byNode: Record<string, CostRecord>;
  byModel: Record<string, CostRecord[]>;
  byAgent: Record<string, CostRecord[]>;
}

/**
 * Execution event for replay
 */
export interface ExecutionEvent {
  id: string;
  timestamp: number;
  type: ExecutionEventType;
  executionId: string;
  nodeId?: string;
  agentId?: string;
  data?: Record<string, unknown>;
  state?: ObservableExecutionState | NodeStatus;
  message?: string;
  input?: unknown;
  output?: unknown;
  error?: string;
  duration?: number;
  tokens?: number;
  cost?: number;
}

/**
 * Execution event types
 */
export enum ExecutionEventType {
  ExecutionStarted = "execution_started",
  ExecutionCompleted = "execution_completed",
  ExecutionFailed = "execution_failed",
  ExecutionCancelled = "execution_cancelled",
  ExecutionPaused = "execution_paused",
  ExecutionResumed = "execution_resumed",
  NodeStarted = "node_started",
  NodeCompleted = "node_completed",
  NodeFailed = "node_failed",
  NodeSkipped = "node_skipped",
  MessageSent = "message_sent",
  MessageReceived = "message_received",
  ToolInvoked = "tool_invoked",
  ToolCompleted = "tool_completed",
  ToolFailed = "tool_failed",
  StateChanged = "state_changed",
  ContextUpdated = "context_updated",
  TokenUsage = "token_usage",
  CostLimitWarning = "cost_limit_warning",
  TokenLimitWarning = "token_limit_warning",
}

/**
 * Graph snapshot for replay
 */
export interface GraphSnapshot {
  timestamp: number;
  eventIndex: number;
  nodeStates: Record<string, NodeSnapshot>;
  messageCount: number;
  totalTokens: number;
  totalCost: number;
}

/**
 * Individual node snapshot
 */
export interface NodeSnapshot {
  nodeId: string;
  name: string;
  role: string;
  status: NodeStatus;
  startTime?: number;
  endTime?: number;
  input?: unknown;
  output?: unknown;
  error?: string;
  messages: AgentMessageRecord[];
  toolCalls: ToolInvocationRecord[];
  tokensUsed?: number;
  costUsed?: number;
}

/**
 * Replay state
 */
export interface ReplayState {
  events: ExecutionEvent[];
  snapshots: GraphSnapshot[];
  currentIndex: number;
  isPlaying: boolean;
  speed: number;
  duration: number;
}

/**
 * Live graph node for visualization
 */
export interface LiveGraphNode {
  id: string;
  name: string;
  role: string;
  status: NodeStatus;
  x?: number;
  y?: number;
  startTime?: number;
  endTime?: number;
  inputTokens?: number;
  outputTokens?: number;
  cost?: number;
  error?: string;
  messages: AgentMessageRecord[];
}

/**
 * Live graph edge for visualization
 */
export interface LiveGraphEdge {
  from: string;
  to: string;
  active: boolean;
  messageCount: number;
  lastMessageTime?: number;
}

/**
 * Budget info
 */
export interface BudgetInfo {
  executionId: string;
  budget: number;
  spent: number;
  remaining: number;
  warningThreshold: number;
  isOverBudget: boolean;
  isWarning: boolean;
  usagePercent: number;
}

/**
 * Observability configuration
 */
export interface ObservabilityConfig {
  enabled: boolean;
  logLevel: ObservableLogLevel;
  maxLogs: number;
  maxToolInvocations: number;
  maxEvents: number;
  captureMessages: boolean;
  captureTokens: boolean;
  captureCost: boolean;
  costLimit?: number;
  tokenLimit?: number;
  warningThreshold: number;
}

/**
 * Default observability config
 */
export const DEFAULT_OBSERVABILITY_CONFIG: ObservabilityConfig = {
  enabled: true,
  logLevel: ObservableLogLevel.Info,
  maxLogs: 10000,
  maxToolInvocations: 5000,
  maxEvents: 10000,
  captureMessages: true,
  captureTokens: true,
  captureCost: true,
  warningThreshold: 80,
};
